import {Checkbox, DatePicker, Input, InputNumber, message, Modal, Select} from "antd"
import axiosInstance from "../../../../../axios/axiosInstance"
import {useEffect, useState} from "react"
import {ModePage} from "common/enum"
import processError from "../../../../../utils/processError"
import dayjs from "dayjs"
import {UserSubscriptionRequest} from "../../../../../model/request";

interface Props {
  isOpen: boolean
  onCloseModal: () => void
  mode: ModePage
  onBeforeSuccess: () => void
  subscriptionId: string
}

interface Plan {
  id: string
  name: string
}

export default function UserSubscriptionFormModal({
                                                    isOpen,
                                                    onCloseModal,
                                                    mode,
                                                    onBeforeSuccess,
                                                    subscriptionId,
                                                  }: Props) {
  const [newSubscription, setNewSubscription] = useState<UserSubscriptionRequest | null>(null)
  const [plans, setPlans] = useState<Plan[]>([])
  const [loadingPlans, setLoadingPlans] = useState(false)

  const onInputField = (e: any, fieldName: string) => {
    const newSubscriptionTemp = {...newSubscription} as any
    newSubscriptionTemp[fieldName] = e.target.value
    setNewSubscription(newSubscriptionTemp)
  }

  const onNumberField = (value: number | null, fieldName: string) => {
    const newSubscriptionTemp = {...newSubscription} as any
    newSubscriptionTemp[fieldName] = value || 0
    setNewSubscription(newSubscriptionTemp)
  }

  const onCheckboxField = (e: any, fieldName: string) => {
    const newSubscriptionTemp = {...newSubscription} as any
    newSubscriptionTemp[fieldName] = e.target.checked
    setNewSubscription(newSubscriptionTemp)
  }

  const onSelectField = (value: any, fieldName: string) => {
    const newSubscriptionTemp = {...newSubscription} as any
    if (fieldName === "userId") {
      newSubscriptionTemp[fieldName] = value
    } else if (fieldName === "plan") {
      newSubscriptionTemp[fieldName] = {id: value}
    }
    setNewSubscription(newSubscriptionTemp)
  }

  const onDateField = (date: any, fieldName: string) => {
    const newSubscriptionTemp = {...newSubscription} as any
    newSubscriptionTemp[fieldName] = date ? date.toISOString() : null
    setNewSubscription(newSubscriptionTemp)
  }

  const loadPlans = async () => {
    setLoadingPlans(true)
    try {
      const res = await axiosInstance({
        url: "plan",
        method: "get",
      })
      if (res.status === 200) {
        setPlans(res.data) // Direct array response, not paginated
      }
    } catch (err) {
      processError(err)
    }
    setLoadingPlans(false)
  }

  const loadNewSubscription = async () => {
    try {
      const now = dayjs()
      const newSubscription: UserSubscriptionRequest = {
        pointFree: 0,
        pointPremium: 0,
        subscriptionDate: now.toISOString(),
        expirationDate: now.add(1, "month").toISOString(),
        userId: "",
        plan: {id: ""},
        pointFreeEnable: true,
        pointPremiumEnable: true,
        isEnable: true,
        isRenewable: false,
      }
      setNewSubscription(newSubscription)
    } catch (err) {
      processError(err)
    }
  }

  const loadEditSubscription = async () => {
    try {
      const res = await axiosInstance({
        url: `/subscription/${subscriptionId}`,
        method: "get",
      })

      if (res.status === 200) {
        setNewSubscription(res.data)
      } else {
        message.error("Có lỗi xảy ra.")
      }
    } catch (err) {
      processError(err)
    }
  }

  const onOk = async () => {
    if (!newSubscription?.userId) {
      message.error("Vui lòng chọn người dùng")
      return
    }
    if (!newSubscription?.plan?.id) {
      message.error("Vui lòng chọn gói")
      return
    }
    try {
      if (newSubscription) {
        await axiosInstance({
          url: mode === ModePage.ADD ? "/subscription" : `/subscription/${subscriptionId}`,
          method: mode === ModePage.ADD ? "post" : "put",
          data: newSubscription,
        })
        onBeforeSuccess()
        message.success(mode === ModePage.ADD ? `Thêm thành công` : `Sửa thành công`)
      }
    } catch (err) {
      processError(err)
    }
  }

  useEffect(() => {
    if (isOpen) {
      loadPlans()
      mode === ModePage.ADD ? loadNewSubscription() : loadEditSubscription()
    }
  }, [isOpen])

  return (
    <Modal
      open={isOpen}
      onOk={onOk}
      onCancel={onCloseModal}
      title={mode === ModePage.ADD ? "Thêm đăng ký mới" : "Chỉnh sửa đăng ký"}
      width={700}
    >
      <div className="mt-8">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block mb-2">
              User ID: <span className="text-red-500">*</span>
            </label>
            <Input
              value={newSubscription?.userId || ''}
              onChange={(e: any) => onInputField(e, "userId")}
              placeholder="Nhập User ID..."
            />
          </div>

          <div>
            <label className="block mb-2">
              Gói: <span className="text-red-500">*</span>
            </label>
            <Select
              style={{width: "100%"}}
              value={newSubscription?.plan?.id}
              onChange={(value) => onSelectField(value, "plan")}
              placeholder="Chọn gói..."
              loading={loadingPlans}
              options={plans.map((plan) => ({
                value: plan.id,
                label: plan.name,
              }))}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block mb-2">Điểm miễn phí:</label>
            <InputNumber
              style={{width: "100%"}}
              value={newSubscription?.pointFree}
              onChange={(value) => onNumberField(value, "pointFree")}
              min={0}
              placeholder="Nhập điểm miễn phí..."
            />
          </div>

          <div>
            <label className="block mb-2">Điểm premium:</label>
            <InputNumber
              style={{width: "100%"}}
              value={newSubscription?.pointPremium}
              onChange={(value) => onNumberField(value, "pointPremium")}
              min={0}
              placeholder="Nhập điểm premium..."
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block mb-2">Ngày đăng ký:</label>
            <DatePicker
              style={{width: "100%"}}
              showTime
              value={newSubscription?.subscriptionDate ? dayjs(newSubscription.subscriptionDate) : null}
              onChange={(date) => onDateField(date, "subscriptionDate")}
              format="DD/MM/YYYY HH:mm"
            />
          </div>

          <div>
            <label className="block mb-2">Ngày hết hạn:</label>
            <DatePicker
              style={{width: "100%"}}
              showTime
              value={newSubscription?.expirationDate ? dayjs(newSubscription.expirationDate) : null}
              onChange={(date) => onDateField(date, "expirationDate")}
              format="DD/MM/YYYY HH:mm"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="space-y-2">
            <div>
              <Checkbox
                checked={!!newSubscription?.pointFreeEnable}
                onChange={(e) => onCheckboxField(e, "pointFreeEnable")}
              >
                Kích hoạt điểm miễn phí
              </Checkbox>
            </div>
            <div>
              <Checkbox
                checked={!!newSubscription?.pointPremiumEnable}
                onChange={(e) => onCheckboxField(e, "pointPremiumEnable")}
              >
                Kích hoạt điểm premium
              </Checkbox>
            </div>
          </div>

          <div className="space-y-2">
            <div>
              <Checkbox checked={!!newSubscription?.isEnable} onChange={(e) => onCheckboxField(e, "isEnable")}>
                Kích hoạt đăng ký
              </Checkbox>
            </div>
            <div>
              <Checkbox checked={!!newSubscription?.isRenewable} onChange={(e) => onCheckboxField(e, "isRenewable")}>
                Có thể gia hạn
              </Checkbox>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}
